import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  Stack,
  TextField,
  Button,
  Typography,
  Paper,
  Avatar,
  IconButton,
  Popover,
  Divider,
  CircularProgress,
  Fade,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { collection, addDoc, query, orderBy, limit, onSnapshot, doc, getDoc, getDocs, startAfter, Query, QuerySnapshot, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { format } from 'date-fns';
import SendIcon from '@mui/icons-material/Send';
import ChatIcon from '@mui/icons-material/Chat';
import LogoutIcon from '@mui/icons-material/Logout';
import { UserList } from './UserList';

interface Message {
  id: string;
  text: string;
  uid: string;
  type?: 'system';
  createdAt: Date;
  userName: string;
}

interface UserData {
  email: string;
  color: string;
  createdAt: Date;
}

export const ChatRoom: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [userColors, setUserColors] = useState<Record<string, string>>({});
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const messagesContainerRef = useRef<null | HTMLDivElement>(null);
  const lastMessageRef = useRef<QuerySnapshot | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const MESSAGES_PER_PAGE = 50;

  // 스크롤 위치 저장을 위한 ref
  const scrollHeightRef = useRef<number>(0);
  const scrollTopRef = useRef<number>(0);

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      if (auth.currentUser) {
        // 온라인 상태 업데이트
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          online: false,
          lastSeen: serverTimestamp()
        });

        // 퇴장 메시지 생성
        await addDoc(collection(db, 'messages'), {
          type: 'system',
          text: `${auth.currentUser.email}님이 퇴장하셨습니다.`,
          createdAt: new Date(),
          uid: 'system'
        });
      }
      
      await auth.signOut();
      enqueueSnackbar('로그아웃 되었습니다.', { variant: 'info' });
    } catch (error) {
      console.error('Error signing out:', error);
      enqueueSnackbar('로그아웃 실패', { variant: 'error' });
    } finally {
      setIsSigningOut(false);
      handleProfileClose();
    }
  };

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // 실시간 메시지 구독
  useEffect(() => {
    const messagesRef = collection(db, 'messages');
    const q = query(
      messagesRef,
      orderBy('createdAt', 'desc'),
      limit(1)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach(async (change) => {
        if (change.type === 'added') {
          const newMessageData = change.doc.data();
          if (newMessageData.uid !== 'system' && !userColors[newMessageData.uid]) {
            await fetchUserColor(newMessageData.uid);
          }
          const newMessage = {
            id: change.doc.id,
            ...newMessageData,
            createdAt: newMessageData.createdAt.toDate()
          } as Message;

          setMessages(prev => {
            if (prev.some(msg => msg.id === newMessage.id)) {
              return prev;
            }
            return [...prev, newMessage].sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
          });

          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
          }
        }
      });
    });

    return () => unsubscribe();
  }, [userColors]);

  // 사용자 색상 정보 가져오기
  const fetchUserColor = async (uid: string) => {
    if (userColors[uid]) return userColors[uid];
    
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const userData = userDoc.data() as UserData;
        setUserColors(prev => ({ ...prev, [uid]: userData.color }));
        return userData.color;
      }
    } catch (error) {
      console.error('Error fetching user color:', error);
    }
    return '#757575'; // 기본 색상
  };

  const loadMessages = useCallback(async (isInitial: boolean = false) => {
    try {
      const messagesRef = collection(db, 'messages');
      let q: Query;

      if (isInitial) {
        q = query(
          messagesRef,
          orderBy('createdAt', 'desc'),
          limit(MESSAGES_PER_PAGE)
        );
      } else {
        if (!lastMessageRef.current) return;
        q = query(
          messagesRef,
          orderBy('createdAt', 'desc'),
          startAfter(lastMessageRef.current.docs[lastMessageRef.current.docs.length - 1]),
          limit(MESSAGES_PER_PAGE)
        );
      }

      const snapshot = await getDocs(q);
      lastMessageRef.current = snapshot;
      setHasMore(snapshot.docs.length === MESSAGES_PER_PAGE);

      const newMessagesPromises = snapshot.docs.map(async (doc) => {
        const data = doc.data();
        if (data.uid !== 'system' && !userColors[data.uid]) {
          await fetchUserColor(data.uid);
        }
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt.toDate()
        } as Message;
      });

      const newMessages = await Promise.all(newMessagesPromises);

      if (isInitial) {
        setMessages(newMessages.reverse());
      } else {
        setMessages(prev => [...newMessages.reverse(), ...prev]);
      }

      return snapshot;
    } catch (error) {
      console.error('Error loading messages:', error);
      enqueueSnackbar('메시지를 불러오는데 실패했습니다.', { variant: 'error' });
    }
  }, [enqueueSnackbar, userColors]);

  useEffect(() => {
    setIsLoading(true);
    loadMessages(true).finally(() => setIsLoading(false));
  }, [loadMessages]);

  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    if (messagesContainerRef.current) {
      scrollHeightRef.current = messagesContainerRef.current.scrollHeight;
      scrollTopRef.current = messagesContainerRef.current.scrollTop;
    }

    await loadMessages(false);
    setLoadingMore(false);

    // 스크롤 위치 복원
    requestAnimationFrame(() => {
      if (messagesContainerRef.current) {
        const newScrollHeight = messagesContainerRef.current.scrollHeight;
        const scrollOffset = newScrollHeight - scrollHeightRef.current;
        messagesContainerRef.current.scrollTop = scrollTopRef.current + scrollOffset;
      }
    });
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!auth.currentUser) {
      enqueueSnackbar('로그인이 필요합니다.', { variant: 'error' });
      return;
    }

    if (newMessage.trim() === '') return;

    const messageToSend = newMessage;
    setNewMessage(''); // 즉시 입력 필드를 비움

    try {
      console.log('Sending message:', {
        text: messageToSend,
        uid: auth.currentUser.uid,
        userName: auth.currentUser.email,
        createdAt: new Date()
      });

      const docRef = await addDoc(collection(db, 'messages'), {
        text: messageToSend,
        uid: auth.currentUser.uid,
        userName: auth.currentUser.email || '익명',
        createdAt: new Date(),
      });

      console.log('Message sent successfully, doc ID:', docRef.id);
    } catch (error) {
      console.error('Error sending message:', error);
      enqueueSnackbar('메시지 전송 실패', { variant: 'error' });
      setNewMessage(messageToSend); // 에러 발생 시 메시지 복원
    }
  };

  const renderMessage = (msg: Message) => {
    if (msg.type === 'system') {
      return (
        <Box
          key={msg.id}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            py: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              bgcolor: 'background.paper',
              px: 2,
              py: 0.5,
              borderRadius: '12px',
            }}
          >
            {msg.text}
          </Typography>
        </Box>
      );
    }

    const isCurrentUser = msg.uid === auth.currentUser?.uid;
    const userColor = userColors[msg.uid] || '#757575';

    return (
      <Box
        key={msg.id}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isCurrentUser ? 'flex-end' : 'flex-start',
          maxWidth: '85%',
          alignSelf: isCurrentUser ? 'flex-end' : 'flex-start',
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ mb: 0.5 }}
        >
          <Avatar
            sx={{
              width: 24,
              height: 24,
              bgcolor: userColor,
              fontSize: '0.875rem',
            }}
          >
            {msg.userName[0].toUpperCase()}
          </Avatar>
          <Typography
            variant="caption"
            color="text.secondary"
          >
            {msg.userName} • {format(msg.createdAt, 'HH:mm')}
          </Typography>
        </Stack>
        <Paper
          elevation={1}
          sx={{
            p: 1.5,
            bgcolor: isCurrentUser ? userColor : 'background.paper',
            color: isCurrentUser ? 'white' : 'text.primary',
            borderRadius: '12px',
            maxWidth: '100%',
            wordBreak: 'break-word',
          }}
        >
          <Typography>{msg.text}</Typography>
        </Paper>
      </Box>
    );
  };

  return (
    <Paper
      elevation={2}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: { xs: '-webkit-fill-available', sm: '100vh' },
        bgcolor: 'background.paper',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: { xs: 0, sm: 2 },
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          position: 'sticky',
          top: 0,
          bgcolor: 'background.paper',
          zIndex: 1100,
          minHeight: { xs: 56, sm: 64 }, // 헤더 최소 높이 지정
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Avatar 
            sx={{ 
              bgcolor: 'primary.main',
              width: { xs: 32, sm: 40 },
              height: { xs: 32, sm: 40 },
            }}
          >
            <ChatIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
          </Avatar>
          <Typography 
            variant="h6"
            sx={{ 
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
              display: 'flex',
              alignItems: 'center',
            }}
          >
            실시간 채팅
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <UserList />
          {auth.currentUser && (
            <>
              <IconButton onClick={handleProfileClick}>
                <Avatar
                  sx={{
                    width: { xs: 32, sm: 40 },
                    height: { xs: 32, sm: 40 },
                    bgcolor: 'secondary.main',
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                  }}
                >
                  {auth.currentUser.email?.[0].toUpperCase()}
                </Avatar>
              </IconButton>
              <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleProfileClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    width: 220,
                    p: 2,
                  }
                }}
              >
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      로그인 계정
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        wordBreak: 'break-all',
                        fontWeight: 500,
                      }}
                    >
                      {auth.currentUser.email}
                    </Typography>
                  </Box>
                  <Divider />
                  <Button
                    onClick={handleSignOut}
                    variant="outlined"
                    color="secondary"
                    startIcon={isSigningOut ? <CircularProgress size={20} /> : <LogoutIcon />}
                    disabled={isSigningOut}
                    fullWidth
                  >
                    {isSigningOut ? '로그아웃 중...' : '로그아웃'}
                  </Button>
                </Stack>
              </Popover>
            </>
          )}
        </Stack>
      </Box>

      {/* Messages */}
      <Box
        ref={messagesContainerRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch', // iOS 스크롤 개선
          p: { xs: 1.5, sm: 2 },
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 1.5, sm: 2 },
          bgcolor: 'grey.50',
          scrollBehavior: 'auto',
          position: 'relative',
          '-webkit-transform': 'translateZ(0)', // iOS 스크롤 성능 개선
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {hasMore && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  pt: 1,
                  pb: 2,
                }}
              >
                <Fade in={!loadingMore}>
                  <Button
                    onClick={handleLoadMore}
                    variant="outlined"
                    size="small"
                    disabled={loadingMore}
                    sx={{
                      borderRadius: '20px',
                      px: 2,
                    }}
                  >
                    {loadingMore ? (
                      <CircularProgress size={20} />
                    ) : (
                      '이전 대화보기'
                    )}
                  </Button>
                </Fade>
              </Box>
            )}
            {messages.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Typography color="text.secondary">
                  아직 메시지가 없습니다. 첫 메시지를 보내보세요!
                </Typography>
              </Box>
            ) : (
              messages.map(renderMessage)
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Message Input */}
      <Box
        component="form"
        onSubmit={sendMessage}
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          position: 'sticky',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          transform: 'translateZ(0)',
          '@supports (-webkit-touch-callout: none)': {
            // iOS Safari에서 키보드가 올라올 때 입력창이 보이도록
            paddingBottom: { xs: '20px', sm: '16px' }
          }
        }}
      >
        <Stack direction="row" spacing={1}>
          <TextField
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="메시지를 입력하세요..."
            fullWidth
            size="medium"
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: 'background.paper',
              },
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (newMessage.trim()) {
                  sendMessage(e);
                }
              }
            }}
          />
          <IconButton
            type="submit"
            color="primary"
            size="large"
            disabled={!newMessage.trim()}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              width: { xs: 42, sm: 48 },
              height: { xs: 42, sm: 48 },
              '&:hover': {
                bgcolor: 'primary.dark',
              },
              '&.Mui-disabled': {
                bgcolor: 'action.disabledBackground',
                color: 'action.disabled',
              },
            }}
          >
            <SendIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
        </Stack>
      </Box>
    </Paper>
  );
}; 